// Registro das fontes do ZentitySelect.
//
// O app tinha um componente de seleção por entidade (pessoa, serviço, conta, categoria, usuário...),
// cada um com sua própria busca, sua própria lista e seu próprio botão de "criar" — e quase sempre
// em duas versões, a antiga e a "-2". Todos faziam a mesma coisa. Aqui a entidade vira DADO: uma
// entrada neste mapa descreve como buscar, como escrever cada linha e como criar um registro novo;
// quem desenha é sempre o zentity-select.svelte.
//
// Acrescentar uma entidade nova é escrever uma entrada aqui, não um componente.

import { CategoryTypeFriendlyEnum } from '../../../modules/financialApi/categories/category.enum'
import dayjs from 'dayjs'
import { faUser } from '@fortawesome/free-solid-svg-icons'
import type { IconDefinition } from '@fortawesome/free-solid-svg-icons'

export type Tone = 'neutral' | 'green' | 'amber' | 'blue' | 'teal'

/** um editor que o "+" pode abrir; mais de um vira um menuzinho de escolha */
export interface EntityCreateOption {
    /** texto do menu quando há mais de uma opção ("Novo serviço", "Novo produto"…) */
    rotulo?: string
    /** título do popover */
    titulo: string
    largura?: string
    altura?: string
    /** importado só ao clicar: o editor costuma ser pesado e evita ciclo de import */
    componente: () => Promise<any>
    props: (atual: any, params: any) => Record<string, any>
}

export interface EntitySourceCreate {
    /** quem não pode criar não vê o botão "+" */
    permitido: () => boolean
    opcoes: (params: any) => EntityCreateOption[]
    /** a entidade salva chega pelo evento de fechar do editor */
    doEvento: (detalhe: any) => any | null
    /** instância em branco passada ao editor antes de salvar */
    vazio: () => any
}

export interface EntitySource<T = any> {
    /** rótulo padrão do campo quando a tela não informa um */
    nome: string
    /** extrai o id da entidade */
    id: (item: T) => string
    /**
     * 'lista' carrega tudo uma vez e escolhe num select — conjuntos pequenos e estáveis
     * (usuários da empresa, categorias de serviço).
     * 'busca' consulta o servidor — conjuntos grandes (pessoas) ou que crescem (contas).
     */
    modo: 'lista' | 'busca'
    carregar?: (params: any) => Promise<T[]>
    buscar?: (query: string, params: any) => Promise<T[]>
    /** hidrata uma entidade que chegou só com o id */
    obter?: (id: string, params: any) => Promise<T>
    /**
     * completa a entidade recém-escolhida na busca. o resultado da busca é o registro cru do servidor,
     * e há entidade que a tela só consegue usar com alguma relação resolvida (o pacote e seus filhos).
     */
    aoEscolher?: (item: T, params: any) => Promise<T>
    /** `params` chega para os casos em que o nome depende do contexto (recurso ocupado, por exemplo) */
    titulo: (item: T, params?: any) => string
    /** segunda linha da opção: o que distingue dois registros de nome parecido */
    detalhes?: (item: T, params?: any) => string[]
    /** etiqueta à direita do nome na lista */
    etiqueta?: (item: T) => string
    etiquetaTom?: (item: T) => Tone
    /** false esconde o "x": há entidades que a tela não pode simplesmente largar */
    podeLimpar?: (item: T) => boolean
    /** modo lista: opção aparece, mas não pode ser escolhida (recurso ocupado, item inativo…) */
    indisponivel?: (item: T, params: any) => boolean
    /** caracteres mínimos para buscar; 0 abre a lista já preenchida */
    minimo?: number
    /** texto quando ainda falta digitar */
    dicaBusca?: string
    /** com um único registro disponível, já vem escolhido (empresa de um atendente só) */
    escolherSeUnico?: boolean
    /**
     * ação sobre o registro JÁ ESCOLHIDO — hoje é o botão que abre a ficha da pessoa, que os
     * seletores antigos tinham. Aparece dentro do campo, à direita, só quando há algo escolhido
     * e `permitido()` devolve true (normalmente uma permissão do usuário).
     */
    abrir?: {
        permitido: () => boolean
        icon: IconDefinition
        /** nome do botão: é o que aparece no hover e o que o leitor de tela anuncia */
        titulo: string
        acao: (item: T, params?: any) => void
    }
    criar?: EntitySourceCreate
}

const fones = (p: any) => (p?.phone ? window.api.utils.utils.phoneFriendly(p.phone) : '')

// o pacote chega com os filhos só como ids (serviceNested), venha ele da busca ou do getOne. quem expande
// o pacote em itens — o PDV, o orçamento — lê `filho.service`, então a relação precisa vir resolvida.
const resolverFilhosDoPacote = async (s: any) => {
    if (Array.isArray(s?.services) && s.services.length > 0) {
        s.services = await window.api.utils.utils.assignServicesFromObject(s.services)
    }
    return s
}
const hhmm = (minutos: number) => dayjs(new Date(0, 0, 0, 0, minutos || 0, 0)).format('HH:mm')

export const entitySources: Record<string, EntitySource> = {
    pessoa: {
        nome: 'Cliente',
        modo: 'busca',
        minimo: 3,
        dicaBusca: 'Digite ao menos 3 caracteres.\nPesquise por nome, e-mail, documento ou telefone.',
        id: (p) => p?.personId,
        titulo: (p) => p?.name || '[sem nome]',
        detalhes: (p) => [fones(p), p?.email || ''],
        buscar: (q) => window.api.events.persons.search(q),
        obter: (id) => window.api.events.persons.getOne(id),
        abrir: {
            permitido: () => !!window.store?.token?.permissions?.persons?.show,
            icon: faUser,
            titulo: 'Abrir a ficha da pessoa',
            acao: (p) => window.person('show', p?.personId),
        },
        criar: {
            permitido: () => !!window.store.token.permissions.persons.persons.write,
            opcoes: () => [
                {
                    titulo: 'Pessoa',
                    largura: '970px',
                    altura: '470px',
                    componente: () => import('../../persons/person-edit.svelte'),
                    props: (atual) => ({ person: atual }),
                },
            ],
            doEvento: (d) => (d?.personId ? d : null),
            vazio: () => ({}),
        },
    },

    usuario: {
        nome: 'Usuário',
        modo: 'lista',
        escolherSeUnico: true,
        id: (u) => u?.userId,
        titulo: (u) => u?.userName || '[usuário excluído]',
        detalhes: (u) => [u?.email || '', fones(u)],
        carregar: () => window.api.users.companies.getCompanyUsers(300),
    },

    meioPagamento: {
        nome: 'Meio de pagamento',
        modo: 'lista',
        id: (p) => p?.paymentMethodId,
        titulo: (p) => p?.label || '',
        carregar: () => window.api.pos.paymentMethods.getAll(),
    },

    grupoAgenda: {
        nome: 'Grupo da agenda',
        modo: 'lista',
        id: (g) => g?.scheduleGroupId,
        titulo: (g) => g?.name || '',
        carregar: () => window.api.schedulers.scheduleGroups.getAll(),
        criar: {
            permitido: () => true,
            opcoes: () => [
                {
                    titulo: 'Grupos de Agendas',
                    largura: '970px',
                    componente: () => import('../../account/schedule-groups/schedule-groups.svelte'),
                    props: () => ({ form: true }),
                },
            ],
            // o editor é a lista de grupos: fecha sem devolver um escolhido, só recarrega
            doEvento: () => null,
            vazio: () => ({}),
        },
    },

    recurso: {
        nome: 'Recurso',
        modo: 'lista',
        id: (r) => r?.resourcesLockId,
        // o motivo vai junto do nome: só desabilitar deixaria o usuário sem saber por quê
        titulo: (r, params) =>
            (r?.label || '') + ((params?.ocupados || []).includes(r?.resourcesLockId) ? ' (bloqueado por outro agendamento)' : ''),
        // ocupado no horário continua na lista, mas cinza: esconder faria parecer que não existe
        indisponivel: (r, params) => (params?.ocupados || []).includes(r?.resourcesLockId),
        carregar: () => window.api.schedulers.resourcesLock.getAll(),
    },

    venda: {
        nome: 'Venda',
        modo: 'lista',
        id: (i) => i?.invoiceId,
        titulo: (i) => dayjs(i?.createdAt).format('DD/MM/YYYY HH:mm') + ' · ' + window.api.utils.utils.toReal(i?.total),
        // a lista é sempre de UMA pessoa; sem ela não há o que listar
        carregar: async (params) => {
            if (!params?.personId) return []
            let vendas = await window.api.pos.invoices.getAll({ personId: params.personId })
            for (const venda of vendas) {
                venda.services = await window.api.utils.utils.assignServicesFromObject(venda.services)
                // o pos-api devolve só o userId de cada item (usuários vivem no banco da users-api),
                // e a tag {vendedor} dos formulários depende do nome resolvido aqui. nullIfNotFound
                // = true porque item sem vendedor é normal, e sem isso cada um forçaria um refetch.
                venda.services = await window.api.utils.utils.assignUsersFromObject(venda.services, false, 'userId', 'user', true)
            }
            // com serviceId, só as vendas que contêm aquele serviço cobrado
            if (params?.serviceId) {
                vendas = vendas.filter((v) => v.services?.some((s) => s.serviceId === params.serviceId && s.value > 0))
            }
            return vendas
        },
    },

    referral: {
        nome: 'Origem',
        modo: 'lista',
        id: (r) => r?.referralId,
        titulo: (r) => r?.label || '',
        carregar: () => window.api.events.referrals.getAll(),
        criar: {
            permitido: () => true,
            opcoes: () => [
                {
                    titulo: 'Origens',
                    largura: '970px',
                    altura: '420px',
                    componente: () => import('../../account/referrals/referrals.svelte'),
                    props: (atual) => ({ referralId: atual?.referralId, form: true }),
                },
            ],
            doEvento: (d) => (d?.referralId ? d : null),
            vazio: () => ({}),
        },
    },

    conta: {
        nome: 'Conta',
        modo: 'busca',
        minimo: 0,
        id: (a) => a?.accountId,
        titulo: (a) => a?.label || '',
        // agência e conta distinguem duas contas do mesmo banco
        detalhes: (a) => [[a?.bank, a?.branch ? 'Ag. ' + a.branch : '', a?.account ? 'Conta ' + a.account : ''].filter(Boolean).join(' · ')],
        buscar: (q, params) => window.api.financials.accounts.getAll({ query: q, pageSize: 100, bank: params?.bank }),
        obter: (id) => window.api.financials.accounts.getOne(id),
        criar: {
            permitido: () => !!window.store.token.permissions.financial.entries.write,
            opcoes: () => [
                {
                    titulo: 'Conta',
                    largura: '970px',
                    altura: '420px',
                    componente: () => import('../../financial/accounts/accounts.svelte'),
                    props: (atual, params) => ({ accountId: atual?.accountId, form: true, bank: params?.bank }),
                },
            ],
            doEvento: (d) => (d?.accountId ? d : null),
            vazio: () => ({}),
        },
    },

    categoriaFinanceira: {
        nome: 'Categoria',
        modo: 'busca',
        minimo: 0,
        id: (c) => c?.categoryId,
        titulo: (c) => c?.label || '',
        // o tipo contábil embaixo: dois planos de conta podem ter rótulos parecidos
        detalhes: (c) => [CategoryTypeFriendlyEnum[c?.type] || ''],
        buscar: (q, params) => window.api.financials.categories.getAll({ query: q, pageSize: 100, cd: params?.creditDebit }),
        obter: (id) => window.api.financials.categories.getOne(id),
        criar: {
            permitido: () => !!window.store.token.permissions.financial.entries.write,
            opcoes: () => [
                {
                    titulo: 'Categoria',
                    largura: '970px',
                    altura: '420px',
                    componente: () => import('../../financial/categories/categories.svelte'),
                    props: (atual, params) => ({ categoryId: atual?.categoryId, form: true, creditDebit: params?.creditDebit }),
                },
            ],
            doEvento: (d) => (d?.categoryId ? d : null),
            vazio: () => ({}),
        },
    },

    categoriaServico: {
        nome: 'Categoria',
        modo: 'lista',
        id: (c) => c?.serviceCategoryId,
        titulo: (c) => c?.name || '',
        carregar: () => window.api.pos.serviceCategories.getAll({ pageSize: 100 }),
        criar: {
            permitido: () => true,
            opcoes: () => [
                {
                    titulo: 'Categorias',
                    largura: '970px',
                    altura: '420px',
                    componente: () => import('../../account/service-categories/service-categories.svelte'),
                    props: (atual) => ({ serviceCategoryId: atual?.serviceCategoryId, form: true }),
                },
            ],
            doEvento: (d) => (d?.serviceCategoryId ? d : null),
            vazio: () => ({}),
        },
    },

    /**
     * Serviço / produto / insumo / pacote — o catálogo do PDV.
     *
     * ATENÇÃO: esta fonte cobre a escolha SIMPLES (catálogo). A escolha com SALDO DO CLIENTE
     * (sessões de pacote já compradas, que gravam parentServicesOnInvoicesId/balanceResultId/
     * invoiceId no item) continua no service-select-2.svelte, que é a variante com saldos usada
     * pela agenda. Não troque um pelo outro sem levar esses três campos junto.
     *
     * params: { type: 'service' | 'product' | 'consumable' | 'all' | combinações com vírgula,
     *           duration: boolean, price: boolean, scheduleId?: string }
     */
    servico: {
        nome: 'Item',
        modo: 'busca',
        minimo: 0,
        id: (s) => s?.serviceId,
        titulo: (s) => (s?.name || '') + (s?.status <= 0 ? ' (excluído)' : ''),
        detalhes: (s, params) => [
            params?.duration && s?.type === 'service' ? 'Duração: ' + hhmm(s.duration) : '',
            params?.price ? 'Preço: ' + (s?.price || 0).toFixed(2) : '',
        ],
        etiqueta: (s) => friendlyServiceType(s?.type),
        etiquetaTom: (s) => serviceTone(s?.type),
        // pacote não se larga pelo "x": os itens filhos já entraram na tela e ficariam órfãos
        podeLimpar: (s) => s?.type !== 'package',
        buscar: async (q, params) => {
            const encontrados = await window.api.pos.services.getAll({ query: q, pageSize: 300, type: (params?.type || 'service').split(',') })
            let lista = ordenarPorTipo(encontrados)
            if (params?.scheduleId) {
                lista = lista.filter((el) => !el.schedulers || el.schedulers.length === 0 || el.schedulers.includes(params.scheduleId))
            }
            return lista
        },
        obter: async (id) => resolverFilhosDoPacote(await window.api.pos.services.getOne(id)),
        // a busca devolve o registro cru: sem isto, escolher um pacote entrega filhos sem `service` e
        // quem expande o pacote quebra ao ler as comissões deles
        aoEscolher: (s) => resolverFilhosDoPacote(s),
        criar: {
            permitido: () => true,
            // com o filtro em um tipo só, o "+" abre direto o editor certo; em "all" (ou combinação)
            // não dá para adivinhar, então o usuário escolhe
            opcoes: (params) => {
                const tipos = (params?.type || 'service').split(',')
                const todas = [
                    {
                        chave: 'service',
                        rotulo: 'Novo serviço',
                        titulo: 'Serviço',
                        largura: '970px',
                        altura: '200px',
                        componente: () => import('../../account/services/services.svelte'),
                        props: (atual) => ({ serviceId: atual?.serviceId, form: true }),
                    },
                    {
                        chave: 'product',
                        rotulo: 'Novo produto',
                        titulo: 'Produto',
                        largura: '970px',
                        altura: '200px',
                        componente: () => import('../../account/products/products.svelte'),
                        props: (atual) => ({ serviceId: atual?.serviceId, form: true }),
                    },
                    {
                        chave: 'consumable',
                        rotulo: 'Novo insumo',
                        titulo: 'Insumo',
                        largura: '970px',
                        altura: '200px',
                        componente: () => import('../../account/consumables/consumables.svelte'),
                        props: (atual) => ({ serviceId: atual?.serviceId, form: true }),
                    },
                ]
                if (tipos.includes('all')) return todas.map(({ chave, ...resto }) => resto)
                const filtradas = todas.filter((el) => tipos.includes(el.chave))
                return (filtradas.length ? filtradas : todas).map(({ chave, ...resto }) => resto)
            },
            doEvento: (d) => (d?.serviceId ? d : null),
            vazio: () => ({}),
        },
    },
}

// a ordem da lista é sempre a mesma: pacote, serviço, produto, insumo
function ordenarPorTipo(lista: any[]) {
    const ordem = ['package', 'service', 'product', 'consumable']
    return ordem.flatMap((t) => lista.filter((el) => el.type === t)).concat(lista.filter((el) => !ordem.includes(el.type)))
}

function friendlyServiceType(type: string) {
    if (type === 'service') return 'Serviço'
    if (type === 'package') return 'Pacote'
    if (type === 'product') return 'Produto'
    if (type === 'consumable') return 'Consumível'
    return type || ''
}

function serviceTone(type: string): Tone {
    if (type === 'service') return 'green'
    if (type === 'package') return 'amber'
    if (type === 'product') return 'blue'
    if (type === 'consumable') return 'teal'
    return 'neutral'
}

export type EntitySourceKey = keyof typeof entitySources
